/**
 * Describes why a run stopped, or null when it ended cleanly. Mirrors the error triage in
 * `Conversation.executeWithLLM` so the notice always agrees with what that method decided
 * to report (or suppress) to Sentry.
 */
export function describeTermination(error: unknown): string | null {
    if (!error) {
        return null;
    }

    const message =
        error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : JSON.stringify(error);

    if (error instanceof Error && error.name === 'AbortError') {
        return formatTerminationMessage('cancelled by user', message);
    }

    if (message.includes('Rate limit exceeded') || message.includes('Daily usage limit reached')) {
        return formatTerminationMessage('rate limited', message);
    }

    if (message.includes('conversation has become too long')) {
        return formatTerminationMessage('context too long', message);
    }

    if (message.includes('Execution was cancelled')) {
        return formatTerminationMessage('cancelled by user', message);
    }

    return formatTerminationMessage('unexpected error', message);
}

/**
 * Formats a stop reason for the transcript. Deliberately plain: the surrounding display-only
 * box already marks it as an annotation, and markdown syntax (`###`) would render it as a
 * heading — larger and louder than the assistant prose it sits beside.
 */
export function formatTerminationMessage(reason: string, detail: string): string {
    return `Agent stopped: ${reason} — ${detail}`;
}
